import { RadioGroup, SxProps } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import {
  useCurrencyLocalization,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";
import {
  Filter,
  FilterDropdown,
  FilterList,
} from "@outschool/ui-components-website";
import { isEqual } from "lodash";
import React from "react";

import usePriceFilterString from "../../hooks/usePriceFilterString";
import FilterActionButtons from "./desktop/FilterActionButtons";

export type PriceFilterProps = {
  open: boolean;
  handleChangesExternally?: boolean;
  handleImplicitFilterChange?: (category: string, value: object) => void;
  onClose: () => void;
  onOpen: () => void;
  onChange?: (value: $TSFixMe) => void;
  onCancel?: () => void;
  element?: React.ReactNode;
  buttonSx?: SxProps;
  priceFilterValue: SearchFiltersType;
  selectedSearchToggle: SearchFilters.SearchToggleType;
};

/**
 * This is new component for contextual price filer. It is used in search page.
 * This will replace PricePerMeetingFilter.tsx
 * @param
 * @returns
 */
export default function PriceFilter({
  open,
  handleChangesExternally,
  handleImplicitFilterChange,
  onClose,
  onOpen,
  onChange,
  element,
  buttonSx,
  onCancel,
  priceFilterValue,
  selectedSearchToggle = SearchFilters.SearchToggleType.ALL,
}: PriceFilterProps) {
  const { t } = useTranslation("client\\components\\search\\PriceFilter");
  const priceOptions = SearchFilters.PRICE_OPTIONS[selectedSearchToggle];
  const isCourseToggleSelected =
    selectedSearchToggle === SearchFilters.SearchToggleType.COURSES;

  // Display the prices differently depending on which tab is selected
  const {
    getTranslatedPriceStrings,
    getTranslatedPricePerSessionStrings,
    getTranslatedPricePerWeekStrings,
  } = usePriceFilterString();

  const priceString =
    selectedSearchToggle === SearchFilters.SearchToggleType.SELF_PACED
      ? getTranslatedPricePerWeekStrings
      : [
          SearchFilters.SearchToggleType.COURSES,
          SearchFilters.SearchToggleType.ONE_TIME,
        ].includes(selectedSearchToggle)
      ? getTranslatedPriceStrings
      : getTranslatedPricePerSessionStrings;

  const valueKeys = {
    max: isCourseToggleSelected ? "pricePerCourseMax" : "pricePerMeetingMax",
    min: isCourseToggleSelected ? "pricePerCourseMin" : "pricePerMeetingMin",
  };
  const [priceMax, priceMin] = isCourseToggleSelected
    ? [priceFilterValue?.pricePerCourseMax, priceFilterValue?.pricePerCourseMin]
    : [
        priceFilterValue?.pricePerMeetingMax,
        priceFilterValue?.pricePerMeetingMin,
      ];

  const { displayPriceMinMax } = useLocalPriceDisplay();
  const { currencyCode } = useCurrencyLocalization();

  const handleFilterChange = (_e: any, value: string) => {
    const { priceMax, priceMin } = JSON.parse(value);
    const parsedValue = priceMax
      ? { [valueKeys.max]: priceMax }
      : priceMin
      ? { [valueKeys.min]: priceMin }
      : {};
    const newFilterValue = {
      pricePerMeetingMax: null,
      pricePerMeetingMin: null,
      pricePerCourseMax: null,
      pricePerCourseMin: null,
      ...parsedValue,
    };
    if (handleChangesExternally) {
      handleImplicitFilterChange?.(
        isCourseToggleSelected ? "pricePerCourse" : "pricePerMeeting",
        newFilterValue,
      );
    }
  };

  return (
    <FilterDropdown
      filterName={t("Price")}
      filterValue={
        isEqual(priceFilterValue, {})
          ? undefined
          : displayPriceMinMax({ priceMax, priceMin } as any, priceString)
      }
      referenceElement={element}
      buttonSx={buttonSx}
      open={open}
      onClose={onClose}
      onOpen={onOpen}
    >
      <FilterList>
        <Filter
          title={
            isCourseToggleSelected
              ? t("Price per course ({{currencyCode}})", { currencyCode })
              : t("Price per class ({{currencyCode}})", { currencyCode })
          }
        >
          <RadioGroup
            name="price"
            value={JSON.stringify({ priceMax, priceMin })}
            options={priceOptions.map(priceOption => ({
              label: displayPriceMinMax(priceOption as any, priceString),
              value: JSON.stringify(priceOption),
            }))}
            onChange={handleFilterChange}
          />
        </Filter>
        {handleChangesExternally ? null : (
          <FilterActionButtons
            onApply={() => onChange?.({ priceMax, priceMin })}
            onCancel={onCancel}
          />
        )}
      </FilterList>
    </FilterDropdown>
  );
}
